import React from "react";
import { ScrollView } from "react-native";
import { useNavigation } from "@react-navigation/native";
import tw from "twrnc";

import Container from "@/components/shared/AppContainer";
import AppHeader from "@/components/shared/AppHeader";
import { LoadingScreen } from "@/components/LoadingScreen";
import Button from "@/components/settings/Button";
import Callout from "@/components/settings/Callout";
import { SettingsPage } from "@/components/settings/SettingsPage";
import { CONTENT_MAX_WIDTH } from "@/components/settings/theme";
import { t } from "@/localization";
import { TEAMS_UI_ENABLED } from "./flag";
import { useTeamAccess } from "./useWorkspace";

// The event a screen's route is about: the event it came with, else its slug.
export const routeEvent = (route) => {
  const params = route?.params || {};
  if (params.event?.slug) return params.event;
  if (params.slug) return { slug: params.slug };
  if (params.relatedEvent?.slug) return params.relatedEvent;
  return null;
};

// `name`: the account's name ("Acme Funeral Home"), if known.
export const NoAccess = ({ name }) => {
  const navigation = useNavigation();
  return (
    <Container>
      <AppHeader />
      <ScrollView
        contentContainerStyle={[
          tw`w-full self-center px-5 pt-2 pb-12`,
          { maxWidth: CONTENT_MAX_WIDTH + 40 },
        ]}
      >
        <SettingsPage title={t("Not available")}>
          <Callout
            tone="info"
            action={
              <Button
                text={t("Back to events")}
                size="sm"
                fullWidth={false}
                onPress={() => navigation.navigate("EventList")}
              />
            }
          >
            {name
              ? t(
                  "Your role in {team}'s account doesn't include this. Ask an admin of the account if you need it.",
                  { team: name }
                )
              : t(
                  "Your role in this account doesn't include this. Ask an admin of the account if you need it."
                )}
          </Callout>
        </SettingsPage>
      </ScrollView>
    </Container>
  );
};

// Teams: a screen opened in someone else's account (from a link, a reload,
// another screen) shows only when your role allows `permission` there. In
// your own account, and for your own events, it's always shown.
const RoleGate = ({ permission, route, children }) => {
  const team = useTeamAccess();
  if (!team.isShared || team.can(permission, routeEvent(route))) {
    return children;
  }
  // A reload in someone else's account: your role there is known once the
  // server has confirmed the account.
  if (team.status !== "active") {
    return (
      <Container>
        <LoadingScreen />
      </Container>
    );
  }
  return <NoAccess name={team.name || null} />;
};

// A screen component behind RoleGate; the component itself while the Teams UI
// is off (the store apps), so nothing changes there.
export function gateScreen(Component, permission) {
  if (!TEAMS_UI_ENABLED) return Component;
  const Gated = (props) => (
    <RoleGate permission={permission} route={props.route}>
      <Component {...props} />
    </RoleGate>
  );
  Gated.displayName = `RoleGate(${
    Component.displayName || Component.name || "Screen"
  })`;
  return Gated;
}

// The same for render callbacks (screens built from EventSubScreen).
export const gateElement = (permission, props, element) =>
  TEAMS_UI_ENABLED ? (
    <RoleGate permission={permission} route={props?.route}>
      {element}
    </RoleGate>
  ) : (
    element
  );

export default RoleGate;
